import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Drawer, IconButton, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import { BORDER, ChangesList, LABEL_SX, Pairs, Section, VALUE_SX } from '../../audit/AuditParts';
import { formatDateTime, hiddenCount, pharmacyLabel, readChanges, toPairs, userLabel, valueToText } from '../../audit/auditFormat';
import { EditEmptyState } from '../edit/EditCommon';
import {
  HISTORY_SCREEN_TITLES, HistoryContext, HistoryRecord, HistoryRow, fetchHistory, fetchHistoryRecord, historyErrorMessage
} from './historyApi';

/** Цвет подписи вида действия: создание, изменение, удаление, массовая операция */
const KIND_COLORS: Record<string, string> = {
  create: '#047857',
  update: '#1d4ed8',
  delete: '#b91c1c',
  bulk: '#b45309'
};

interface ListState {
  rows: HistoryRow[];
  total: number;
  page: number;
  pages: number;
  canDetails: boolean;
}

const EMPTY_LIST: ListState = { rows: [], total: 0, page: 0, pages: 1, canDetails: false };

/** Подпись выбранного на экране контекста: «Аптека ap1 · 2026-10» */
function contextLabel(context: HistoryContext): string {
  return [context.pharmacy ? `Аптека ${context.pharmacy}` : '', context.period || ''].filter(Boolean).join(' · ');
}

/** Объект, аптека и период записи одной строкой */
function subjectLine(row: HistoryRow): string {
  const pharmacy = pharmacyLabel(row);
  return [row.entityLabel, pharmacy ? `Аптека ${pharmacy}` : '', row.period].filter(Boolean).join(' · ');
}

const DetailsBody: React.FC<{ record: HistoryRecord }> = ({ record }) => {
  const details = record.details;
  const changes = readChanges(details);
  const snapshot = toPairs(details ? details.snapshot : null);
  const ids = details && Array.isArray(details.ids) ? details.ids : [];
  const idsHidden = hiddenCount(ids.length, details ? details.total : undefined);

  if (changes.length === 0 && snapshot.length === 0 && ids.length === 0) {
    return (
      <Typography sx={LABEL_SX}>
        {details && details.truncated ? 'Данные слишком велики и сохранены не полностью' : 'Подробностей по этой записи нет'}
      </Typography>
    );
  }

  return (
    <>
      {changes.length > 0 && (
        <Section title="Что изменилось">
          <ChangesList changes={changes} total={details ? details.changesTotal : undefined} />
        </Section>
      )}
      {snapshot.length > 0 && (
        <Section title="Данные записи на момент действия">
          <Pairs pairs={snapshot} />
        </Section>
      )}
      {ids.length > 0 && (
        <Section title={`Затронутые записи: ${details && details.total !== undefined ? details.total : ids.length}`}>
          <Typography sx={{ ...VALUE_SX, color: '#334155' }}>{ids.map((id) => valueToText(id)).join(', ')}</Typography>
          {idsHidden > 0 && <Typography sx={{ ...LABEL_SX, mt: 0.5 }}>Показаны первые {ids.length}, еще {idsHidden}</Typography>}
        </Section>
      )}
    </>
  );
};

/** Одна запись истории; подробности («было → стало») загружаются при раскрытии */
const HistoryItem: React.FC<{ row: HistoryRow; canDetails: boolean }> = ({ row, canDetails }) => {
  const [open, setOpen] = useState(false);
  const [record, setRecord] = useState<HistoryRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const loadRecord = () => {
    setLoading(true);
    setError('');
    fetchHistoryRecord(row.id)
      .then((res) => {
        if (alive.current) setRecord(res);
      })
      .catch((err) => {
        if (alive.current) setError(historyErrorMessage(err, 'Не удалось загрузить подробности'));
      })
      .then(() => {
        if (alive.current) setLoading(false);
      });
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !record && !loading) loadRecord();
  };

  const expandable = canDetails && row.hasDetails;
  const subject = subjectLine(row);

  return (
    <Box sx={{ py: 1.5, borderBottom: '1px solid #f1f5f9' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ ...LABEL_SX, flex: 1 }}>{formatDateTime(row.createdAt)}</Typography>
        <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: KIND_COLORS[row.kind] || '#475569' }}>{row.kindTitle}</Typography>
      </Box>
      <Typography sx={{ ...VALUE_SX, fontWeight: 700, mt: 0.25 }}>{row.actionTitle}</Typography>
      <Typography sx={{ ...LABEL_SX, mt: 0.25 }}>
        {userLabel(row)}
        {row.roleName ? ` · ${row.roleName}` : ''}
        {row.actorUsername ? ` · выполнено администратором ${row.actorUsername}` : ''}
      </Typography>
      {subject && <Typography sx={{ ...VALUE_SX, color: '#334155', mt: 0.25 }}>{subject}</Typography>}
      {row.summary && <Typography sx={{ ...VALUE_SX, color: '#334155', mt: 0.25 }}>{row.summary}</Typography>}

      {expandable && (
        <Button
          size="small"
          onClick={toggle}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', px: 0.5, mt: 0.5, minWidth: 0 }}
        >
          {open ? 'Скрыть подробности' : 'Что изменилось'}
        </Button>
      )}

      {open && (
        <Box sx={{ mt: 1.5 }}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          )}
          {!loading && error && (
            <Box>
              <Typography sx={{ color: '#b91c1c', fontSize: '0.85rem', mb: 0.5 }}>{error}</Typography>
              <Button size="small" onClick={loadRecord} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>
            </Box>
          )}
          {!loading && record && <DetailsBody record={record} />}
        </Box>
      )}
    </Box>
  );
};

interface Props {
  context: HistoryContext;
  onClose: () => void;
}

/** Правая панель «История» экрана отчётности: кто, когда и что изменил, с раскрытием «было → стало» */
const ReportHistoryDrawer: React.FC<Props> = ({ context, onClose }) => {
  const hasContext = Boolean(context.pharmacy || context.period);
  const [onlyContext, setOnlyContext] = useState(hasContext);
  const [list, setList] = useState<ListState>(EMPTY_LIST);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState('');
  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  // Номер текущей загрузки: ответ устаревшего запроса (смена фильтра, обновление, закрытие) отбрасывается
  const generation = useRef(0);

  const screen = context.screen;
  const pharmacy = onlyContext ? context.pharmacy || '' : '';
  const period = onlyContext ? context.period || '' : '';

  useEffect(() => {
    generation.current += 1;
    const current = generation.current;
    setStatus('loading');
    setError('');
    setList(EMPTY_LIST);
    setMoreLoading(false);
    setMoreError('');

    fetchHistory({ screen, pharmacy, period }, 1)
      .then((res) => {
        if (generation.current !== current) return;
        setList({ rows: res.rows, total: res.total, page: res.page, pages: res.pages, canDetails: res.canDetails });
        setStatus('ready');
      })
      .catch((err) => {
        if (generation.current !== current) return;
        setError(historyErrorMessage(err, 'Не удалось загрузить историю'));
        setStatus('error');
      });

    return () => {
      generation.current += 1;
    };
  }, [screen, pharmacy, period, reloadKey]);

  const loadMore = () => {
    const current = generation.current;
    setMoreLoading(true);
    setMoreError('');
    fetchHistory({ screen, pharmacy, period }, list.page + 1)
      .then((res) => {
        if (generation.current !== current) return;
        // Пока открыта панель, могли появиться новые записи: страницы сдвигаются, поэтому повторы отбрасываются
        setList((prev) => {
          const known: Record<number, boolean> = {};
          prev.rows.forEach((row) => {
            known[row.id] = true;
          });
          return { ...res, rows: prev.rows.concat(res.rows.filter((row) => !known[row.id])) };
        });
      })
      .catch((err) => {
        if (generation.current !== current) return;
        setMoreError(historyErrorMessage(err, 'Не удалось загрузить следующие записи'));
      })
      .then(() => {
        if (generation.current === current) setMoreLoading(false);
      });
  };

  const hasMore = list.page < list.pages;

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, display: 'flex', flexDirection: 'column' } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, px: 3, py: 2, backgroundColor: '#ffffff', borderBottom: BORDER }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>История изменений</Typography>
          <Typography sx={{ ...LABEL_SX, mt: 0.25 }}>
            {HISTORY_SCREEN_TITLES[screen]}
            {status === 'ready' ? ` · записей: ${list.total}` : ''}
          </Typography>
        </Box>
        <Tooltip title="Обновить">
          <span>
            <IconButton onClick={() => setReloadKey((key) => key + 1)} disabled={status === 'loading'} size="small" aria-label="Обновить" sx={{ color: '#64748b' }}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
        <IconButton onClick={onClose} size="small" aria-label="Закрыть" sx={{ color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {hasContext && (
        <Box sx={{ px: 3, py: 1.5, borderBottom: BORDER, backgroundColor: '#f8fafc' }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={onlyContext ? 'context' : 'all'}
            onChange={(_, value: string | null) => {
              if (value) setOnlyContext(value === 'context');
            }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.8rem', fontWeight: 600, px: 1.5, py: 0.4 } }}
          >
            <ToggleButton value="context">{contextLabel(context)}</ToggleButton>
            <ToggleButton value="all">Весь экран</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 1.5 }}>
        {status === 'loading' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {status === 'error' && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ color: '#b91c1c', fontSize: '0.9rem', mb: 1.5 }}>{error}</Typography>
            <Button onClick={() => setReloadKey((key) => key + 1)} sx={{ textTransform: 'none', fontWeight: 600 }}>Повторить</Button>
          </Box>
        )}

        {status === 'ready' && list.rows.length === 0 && (
          <Box sx={{ py: 3 }}>
            <EditEmptyState
              icon={<HistoryIcon />}
              title="Изменений пока нет"
              text={hasContext && onlyContext
                ? 'По выбранной аптеке и периоду изменений не найдено. Переключитесь на «Весь экран», чтобы увидеть всю историю.'
                : 'История этого экрана пуста или недоступна для ваших аптек.'}
            />
          </Box>
        )}

        {status === 'ready' && list.rows.map((row) => <HistoryItem key={row.id} row={row} canDetails={list.canDetails} />)}

        {status === 'ready' && list.rows.length > 0 && hasMore && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {moreError && <Typography sx={{ color: '#b91c1c', fontSize: '0.85rem', mb: 1 }}>{moreError}</Typography>}
            <Button onClick={loadMore} disabled={moreLoading} sx={{ textTransform: 'none', fontWeight: 600 }}>
              {moreLoading ? 'Загрузка...' : `Показать еще (${list.total - list.rows.length})`}
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default ReportHistoryDrawer;
